import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { TVHeader } from '@/components/tv-mode/TVHeader';
import { TVTournamentColumn } from '@/components/tv-mode/TVTournamentColumn';
import { TVUpcomingSidebar } from '@/components/tv-mode/TVUpcomingSidebar';
import { TVResultsBottom } from '@/components/tv-mode/TVResultsBottom';
import {
  TV_PALETTE,
  TV_RESULTS_MAX_ITEMS,
  TV_UPCOMING_COLUMN_WIDTH,
} from '@/components/tv-mode/tv-mode.constants';
import {
  TVScaleProvider,
  useTVScale,
} from '@/components/tv-mode/TVScaleContext';
import { useTVTournamentData } from '@/hooks/useTVTournamentData';
import {
  resolveTvFixturePresentation,
} from '@/components/tv-mode/tv-phase-detection';
import {
  buildResultsForTournament,
  buildUpcomingMatches,
  resolveClubInfo,
} from '@/utils/tv-mode-data';

const MAX_TOURNAMENTS = 2;

export default function TVModeViewPage() {
  return (
    <TVScaleProvider>
      <TVModeViewPageInner />
    </TVScaleProvider>
  );
}

function TVModeViewPageInner() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const {
    s,
    mult,
    getTeamName,
    nameMode,
    upcomingColumnWidth,
    setUpcomingColumnWidth,
  } = useTVScale();

  const ids = useMemo(() => {
    const raw = searchParams.get('ids') ?? '';
    return raw
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean)
      .slice(0, MAX_TOURNAMENTS);
  }, [searchParams]);

  const { data, isInitialLoading, error } = useTVTournamentData(ids);

  useEffect(() => {
    if (ids.length === 0) navigate('/tv', { replace: true });
  }, [ids, navigate]);

  const tournaments = data.map((d) => d.tournament);
  const clubInfo = useMemo(() => resolveClubInfo(tournaments), [tournaments]);
  const upcoming = useMemo(
    () => buildUpcomingMatches(data, 8, getTeamName),
    [data, getTeamName, nameMode],
  );

  const singleMode = data.length === 1;
  const tournamentGridTemplate = singleMode ? '1fr' : '1fr 1fr';

  const resultsColumns = useMemo(
    () =>
      data.map((d, idx) => {
        const presentation = resolveTvFixturePresentation(d.fixture);
        return {
          tournamentName: d.tournament.name,
          animationDelay: idx === 0 ? undefined : '0.5s',
          results: buildResultsForTournament(
            d.fixture,
            TV_RESULTS_MAX_ITEMS,
            { onlyKnockout: presentation === 'knockout-only' },
            getTeamName,
          ),
          phase: presentation,
        };
      }),
    [data, getTeamName, nameMode],
  );

  return (
    <div
      className="tv-mode"
      style={{
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        height: '100vh',
        overflow: 'hidden',
        background: TV_PALETTE.bg,
      }}
    >
      <TVHeader clubInfo={clubInfo} onExit={() => navigate('/tv')} />

      {isInitialLoading ? (
        <div
          style={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: TV_PALETTE.textMuted,
            fontSize: s(14),
          }}
        >
          Cargando información en vivo…
        </div>
      ) : data.length === 0 ? (
        <div
          style={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: TV_PALETTE.textMuted,
            fontSize: s(14),
            textAlign: 'center',
            padding: s(24),
          }}
        >
          {error ?? 'No hay información para mostrar.'}
        </div>
      ) : (
        <>
          <div
            style={{
              flex: 1,
              display: 'flex',
              flexDirection: 'row',
              minHeight: 0,
              overflowX: 'hidden',
              overflowY: 'visible',
            }}
          >
            <div
              style={{
                flex: 1,
                minWidth: 0,
                minHeight: 0,
                display: 'flex',
                flexDirection: 'column',
                overflowX: 'hidden',
                /* Deja ver el halo del spotlight en “Últimos resultados” (box-shadow) */
                overflowY: 'visible',
              }}
            >
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: tournamentGridTemplate,
                  gridTemplateRows: 'minmax(0, 1fr)',
                  flex: 1,
                  minHeight: 0,
                  overflow: 'hidden',
                }}
              >
                {data.map((d, idx) => (
                  <div
                    key={d.tournament.id}
                    style={{
                      padding:
                        idx === 0
                          ? `${s(14)}px ${s(14)}px ${s(10)}px ${s(16)}px`
                          : `${s(14)}px ${s(14)}px ${s(10)}px`,
                      borderRight:
                        idx < data.length - 1
                          ? `1px solid ${TV_PALETTE.border}`
                          : undefined,
                      overflow: 'hidden',
                      display: 'flex',
                      flexDirection: 'column',
                      minHeight: 0,
                    }}
                  >
                    <TVTournamentColumn
                      tournament={d.tournament}
                      fixture={d.fixture}
                      accent={TV_PALETTE.accent}
                      accent2={TV_PALETTE.accent2}
                      layout={singleMode ? 'single' : 'dual'}
                    />
                  </div>
                ))}
              </div>
              <TVResultsBottom columns={resultsColumns} />
            </div>
            <div
              style={{
                width: s(upcomingColumnWidth),
                flexShrink: 0,
                position: 'relative',
                padding: `${s(14)}px ${s(16)}px ${s(10)}px ${s(14)}px`,
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                minHeight: 0,
                borderLeft: `1px solid ${TV_PALETTE.border}`,
                boxSizing: 'border-box',
                background: TV_PALETTE.bg,
              }}
            >
              <TVUpcomingResizer
                currentWidth={upcomingColumnWidth}
                mult={mult}
                onResize={setUpcomingColumnWidth}
              />
              <TVUpcomingSidebar
                matches={upcoming}
                accent={TV_PALETTE.accent}
              />
            </div>
          </div>
        </>
      )}
    </div>
  );
}

/**
 * Handle vertical pegado al borde izquierdo de la columna “Próximos partidos”.
 * Arrastrar a izquierda → más ancho; el ancho persiste vía TVScaleContext.
 * Doble click → reset al default. La conversión px‑pantalla → px‑base usa
 * `mult` para que el ajuste sea consistente con la escala activa.
 */
function TVUpcomingResizer({
  currentWidth,
  mult,
  onResize,
}: {
  currentWidth: number;
  mult: number;
  onResize: (next: number) => void;
}) {
  const dragRef = useRef<{ startX: number; startWidth: number } | null>(null);

  const onPointerDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      e.preventDefault();
      const target = e.currentTarget;
      target.setPointerCapture(e.pointerId);
      dragRef.current = { startX: e.clientX, startWidth: currentWidth };
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    },
    [currentWidth],
  );

  const onPointerMove = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      const drag = dragRef.current;
      if (!drag) return;
      // Mover a la izquierda agranda: invertimos el delta. Convertimos a px
      // pre‑escala dividiendo por el multiplicador activo.
      const deltaPx = (drag.startX - e.clientX) / Math.max(0.001, mult);
      onResize(drag.startWidth + deltaPx);
    },
    [mult, onResize],
  );

  const endDrag = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (!dragRef.current) return;
      dragRef.current = null;
      try {
        e.currentTarget.releasePointerCapture(e.pointerId);
      } catch {
        /* ignore */
      }
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    },
    [],
  );

  return (
    <div
      role="separator"
      aria-orientation="vertical"
      aria-label="Redimensionar columna de próximos partidos"
      title="Arrastrá para redimensionar — doble click para restablecer"
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={endDrag}
      onPointerCancel={endDrag}
      onDoubleClick={() => onResize(TV_UPCOMING_COLUMN_WIDTH)}
      style={{
        position: 'absolute',
        top: 0,
        bottom: 0,
        left: -3,
        width: 8,
        cursor: 'col-resize',
        zIndex: 30,
        touchAction: 'none',
      }}
    >
      <div
        style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 3,
          height: 36,
          borderRadius: 2,
          background: TV_PALETTE.border,
          opacity: 0.6,
          pointerEvents: 'none',
          transition: 'opacity 0.15s, background 0.15s',
        }}
      />
    </div>
  );
}
